import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientQuery } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';
import { ExceptionManager } from 'Core/logManager';

const SLICE_NAME = 'devices/views/main';

// Initial state
const INIT_STATE = {
    loading: false,
    devices: [],
    total: 0, // DEPRECATED: main query no longer returns total. Kept for non-infinite-scroll compatibility.
    nextToken: null,
    error: null,
    lastRequestFilters: null, // Track filters to prevent duplicate calls with same conditions
    // Total count query (separate from main query)
    totalCount: 0,
    totalCountLoading: false,
};

/**
 * Async thunk for fetching devices
 * Receives the pre-built query from DataViewInfiniteScroll's onFetchRequest callback
 */
export const fetchDevicesMain = createAsyncThunk(
    `${SLICE_NAME}/fetchDevicesMain`,
    async (payload, { rejectWithValue }) => {
        try {
            const { 
                query,      // Pre-built GraphQL query from DataViewInfiniteScroll
                limit, 
                offset, 
                sort, 
                conditions,
                isLoadMore 
            } = payload;

            const queryParams = {
                conditions: conditions || [],
                sort: sort || [{ field: "id", order: "ASC" }],
                limit: limit || 50,
                offset: offset || 0,
            };

            const data = await clientQuery(
                query,
                queryParams,
                endpoints.GRAPHQL_GENERAL,
            );

            let devices = [];

            if (data && data?.result) {
                devices = data.result.items || [];
            }

            return { 
                devices, 
                nextToken: data?.result?.nextToken || null, 
                isLoadMore 
            };

        } catch (exc) {
            ExceptionManager(
                exc,
                "Modules/devices/views/main/redux/slice",
                "fetchDevicesMain"
            );
            return rejectWithValue(exc.message || 'Failed to fetch devices');
        }
    },
    {
        // Condition to prevent duplicate calls with same filters while loading
        condition: (payload, { getState }) => {
            const { devicesMainRedux } = getState();
            const { loading, lastRequestFilters } = devicesMainRedux;
            
            // If already loading with the same filters, skip this call
            if (loading && JSON.stringify(payload.conditions) === JSON.stringify(lastRequestFilters)) {
                return false; // Prevents execution
            }
            return true; // Allow execution
        },
    }
);

/**
 * Async thunk for fetching total count (separate from main query)
 * Fires on initial load and filter changes, NOT on sort changes or load more
 */
export const fetchDevicesMainTotalCount = createAsyncThunk(
    `${SLICE_NAME}/fetchDevicesMainTotalCount`,
    async (payload, { rejectWithValue }) => {
        try {
            const { query, conditions, sort, limit, offset } = payload;
            const queryParams = {
                conditions: conditions || [],
                sort: sort || [{ field: "id", order: "ASC" }],
                limit: limit || 50,
                offset: offset || 0,
            };

            const data = await clientQuery(
                query,
                queryParams,
                endpoints.GRAPHQL_GENERAL,
            );

            return { total: data?.result?.total || 0 };
        } catch (exc) {
            ExceptionManager(
                exc,
                "Modules/devices/views/main/redux/slice",
                "fetchDevicesMainTotalCount"
            );
            return rejectWithValue(exc.message || 'Failed to fetch total count');
        }
    }
);

// Create the slice
export const devicesMainSlice = createSlice({
    name: SLICE_NAME,
    initialState: INIT_STATE,
    reducers: {
        // Reset state (full or partial)
        reset(state, action) {
            return action.payload 
                ? { ...state, ...action.payload } 
                : { ...INIT_STATE };
        },
        // Clear devices list (preserves totalCount — only updated by fetchDevicesMainTotalCount thunk)
        clearDevices(state) {
            state.devices = [];
            state.total = 0; // DEPRECATED: kept for non-infinite-scroll compatibility
            state.nextToken = null;
        },
        // Set loading state manually if needed
        setLoading(state, action) {
            state.loading = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDevicesMain.pending, (state, action) => {
                state.loading = true;
                state.error = null;
                // Track filters to detect duplicate calls
                state.lastRequestFilters = action.meta.arg.conditions;
            })
            .addCase(fetchDevicesMain.fulfilled, (state, action) => {
                state.loading = false;
                // CRITICAL: Append or replace based on isLoadMore
                state.devices = action.payload.isLoadMore
                    ? [...state.devices, ...action.payload.devices]
                    : action.payload.devices;
                // total no longer returned by main query — state.total stays at 0
                state.nextToken = action.payload.nextToken;
            })
            .addCase(fetchDevicesMain.rejected, (state, action) => {
                state.loading = false;
                // Don't set error if request was aborted (user changed filters)
                if (!action.meta.aborted) {
                    state.error = action.payload || action.error.message;
                }
            })
            // Total count thunk cases
            .addCase(fetchDevicesMainTotalCount.pending, (state) => {
                state.totalCountLoading = true;
            })
            .addCase(fetchDevicesMainTotalCount.fulfilled, (state, action) => {
                state.totalCountLoading = false;
                state.totalCount = action.payload.total;
            })
            .addCase(fetchDevicesMainTotalCount.rejected, (state) => {
                state.totalCountLoading = false;
            });
    }
});

// Export actions
export const { 
    reset: resetDevicesMain, 
    clearDevices: clearDevicesMain,
    setLoading: setDevicesMainLoading 
} = devicesMainSlice.actions;

// Export reducer
export default devicesMainSlice.reducer;

// Selector helpers (state key is devicesMainRedux as registered in reducers.js)
export const selectDevicesMain = (state) => state.devicesMainRedux;
export const selectDevices = (state) => state.devicesMainRedux?.devices || [];
export const selectDevicesLoading = (state) => state.devicesMainRedux?.loading;
export const selectDevicesTotal = (state) => state.devicesMainRedux?.total || 0;
export const selectDevicesNextToken = (state) => state.devicesMainRedux?.nextToken;
export const selectDevicesTotalCount = (state) => state.devicesMainRedux?.totalCount || 0;
export const selectDevicesTotalCountLoading = (state) => state.devicesMainRedux?.totalCountLoading;
